import React, { useState, useEffect } from 'react';
import {
  Phone, PhoneCall, Settings, Server, Shield, Volume2,
  Headphones, Mic, Users, Clock, Zap, Database, Key,
  Globe, Network, Router, Speaker, AudioLines, Video,
  Save, RefreshCw, TestTube, CheckCircle, AlertTriangle,
  Plus, Trash2, Edit, Eye, EyeOff, BarChart3
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import toast from 'react-hot-toast';

interface VoIPServerConfig {
  id: string;
  name: string;
  type: 'stun' | 'turn' | 'sip';
  host: string;
  port: number;
  username?: string;
  password?: string;
  protocol: 'udp' | 'tcp' | 'tls';
  is_active: boolean;
}

interface CodecConfig {
  id: string;
  name: string;
  type: 'audio' | 'video';
  codec: string;
  priority: number;
  bitrate?: number;
  sample_rate?: number;
  is_enabled: boolean;
}

interface VoIPSettings {
  // WebRTC Configuration
  webrtc: {
    ice_servers: VoIPServerConfig[];
    gathering_timeout: number;
    connection_timeout: number;
    reconnect_attempts: number;
  };

  // SIP Configuration
  sip: {
    domain: string;
    proxy_server: string;
    registration_server: string;
    outbound_proxy?: string;
    transport: 'udp' | 'tcp' | 'tls';
    port: number;
    auth_username: string;
    auth_password: string;
    display_name: string;
  };

  // Audio/Video Configuration
  media: {
    audio_codecs: CodecConfig[];
    video_codecs: CodecConfig[];
    audio_constraints: {
      echo_cancellation: boolean;
      noise_suppression: boolean;
      auto_gain_control: boolean;
      sample_rate: number;
      channel_count: number;
    };
    video_constraints: {
      width: number;
      height: number;
      frame_rate: number;
      aspect_ratio: string;
    };
  };

  // Call Management
  call_settings: {
    max_call_duration: number; // minutes
    ring_timeout: number; // seconds
    call_hold_timeout: number; // minutes
    auto_answer_delay: number; // seconds
    call_recording_enabled: boolean;
    call_transfer_enabled: boolean;
    conference_enabled: boolean;
    max_conference_participants: number;
  };

  // Queue Configuration
  queue_settings: {
    max_queue_size: number;
    max_wait_time: number; // minutes
    queue_timeout: number; // seconds
    priority_routing: boolean;
    overflow_action: 'voicemail' | 'redirect' | 'hangup';
    overflow_destination?: string;
  };

  // Security & Compliance
  security: {
    encryption_enabled: boolean;
    srtp_enabled: boolean;
    dtls_enabled: boolean;
    certificate_path?: string;
    private_key_path?: string;
    allowed_domains: string[];
    blocked_numbers: string[];
    rate_limiting: {
      calls_per_minute: number;
      calls_per_hour: number;
    };
  };

  // Monitoring & Logging
  monitoring: {
    call_quality_monitoring: boolean;
    call_recording_path: string;
    log_level: 'debug' | 'info' | 'warn' | 'error';
    metrics_enabled: boolean;
    health_check_interval: number; // seconds
    alert_thresholds: {
      packet_loss: number; // percentage
      jitter: number; // ms
      latency: number; // ms
    };
  };
}

const inputCls = 'w-full px-3 py-2 border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none';
const labelCls = 'block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1';
const checkboxCls = 'h-4 w-4 border-slate-300 text-primary-600 focus:ring-primary-500';

const serverAccent = (type: VoIPServerConfig['type']) =>
  type === 'stun' ? 'border-t-primary-600' : type === 'turn' ? 'border-t-gold-500' : 'border-t-slate-400';

const VoIPSettingsPage = () => {
  const [settings, setSettings] = useState<VoIPSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [activeTab, setActiveTab] = useState('webrtc');
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  const [testResults, setTestResults] = useState<Record<string, any>>({});

  // Mock initial settings
  const defaultSettings: VoIPSettings = {
    webrtc: {
      ice_servers: [
        {
          id: 'stun-1',
          name: 'Google STUN Server',
          type: 'stun',
          host: 'stun.l.google.com',
          port: 19302,
          protocol: 'udp',
          is_active: true
        },
        {
          id: 'turn-1',
          name: 'Company TURN Server',
          type: 'turn',
          host: 'turn.company.com',
          port: 3478,
          username: 'turnuser',
          password: 'turnpass123',
          protocol: 'udp',
          is_active: true
        }
      ],
      gathering_timeout: 30000,
      connection_timeout: 20000,
      reconnect_attempts: 3
    },
    sip: {
      domain: 'company.com',
      proxy_server: 'sip.company.com',
      registration_server: 'sip.company.com',
      outbound_proxy: 'proxy.company.com',
      transport: 'udp',
      port: 5060,
      auth_username: 'sipuser',
      auth_password: 'sippass123',
      display_name: 'Call Center'
    },
    media: {
      audio_codecs: [
        { id: 'opus', name: 'Opus', type: 'audio', codec: 'opus', priority: 1, bitrate: 64000, sample_rate: 48000, is_enabled: true },
        { id: 'g722', name: 'G.722', type: 'audio', codec: 'G722', priority: 2, bitrate: 64000, sample_rate: 16000, is_enabled: true },
        { id: 'pcmu', name: 'PCMU', type: 'audio', codec: 'PCMU', priority: 3, bitrate: 64000, sample_rate: 8000, is_enabled: true }
      ],
      video_codecs: [
        { id: 'vp8', name: 'VP8', type: 'video', codec: 'VP8', priority: 1, bitrate: 1000000, is_enabled: true },
        { id: 'h264', name: 'H.264', type: 'video', codec: 'H264', priority: 2, bitrate: 1500000, is_enabled: false }
      ],
      audio_constraints: {
        echo_cancellation: true,
        noise_suppression: true,
        auto_gain_control: true,
        sample_rate: 48000,
        channel_count: 1
      },
      video_constraints: {
        width: 1280,
        height: 720,
        frame_rate: 30,
        aspect_ratio: '16:9'
      }
    },
    call_settings: {
      max_call_duration: 60,
      ring_timeout: 30,
      call_hold_timeout: 5,
      auto_answer_delay: 0,
      call_recording_enabled: true,
      call_transfer_enabled: true,
      conference_enabled: true,
      max_conference_participants: 10
    },
    queue_settings: {
      max_queue_size: 50,
      max_wait_time: 10,
      queue_timeout: 300,
      priority_routing: true,
      overflow_action: 'voicemail',
      overflow_destination: 'voicemail@skyview-assets.de'
    },
    security: {
      encryption_enabled: true,
      srtp_enabled: true,
      dtls_enabled: true,
      certificate_path: '/etc/ssl/certs/voip.crt',
      private_key_path: '/etc/ssl/private/voip.key',
      allowed_domains: ['company.com', '*.company.com'],
      blocked_numbers: ['+90555000000'],
      rate_limiting: {
        calls_per_minute: 60,
        calls_per_hour: 1000
      }
    },
    monitoring: {
      call_quality_monitoring: true,
      call_recording_path: '/var/lib/voip/recordings',
      log_level: 'info',
      metrics_enabled: true,
      health_check_interval: 30,
      alert_thresholds: {
        packet_loss: 5,
        jitter: 50,
        latency: 150
      }
    }
  };

  useEffect(() => {
    const loadSettings = async () => {
      setIsLoading(true);
      try {
        // TODO: Load from API
        // const response = await adminApi.voip.getSettings();
        // setSettings(response.data);

        // Mock data for now
        await new Promise(resolve => setTimeout(resolve, 1000));
        setSettings(defaultSettings);
      } catch (err) {
        toast.error('VoIP ayarları yüklenirken hata oluştu');
        setSettings(defaultSettings);
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, []);

  const saveSettings = async () => {
    if (!settings) return;

    setSaving(true);
    try {
      // TODO: Save to API
      // await adminApi.voip.updateSettings(settings);

      await new Promise(resolve => setTimeout(resolve, 2000));
      toast.success('VoIP ayarları başarıyla kaydedildi');
    } catch (err) {
      toast.error('Ayarlar kaydedilirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async (type: 'webrtc' | 'sip' | 'media') => {
    setIsTesting(true);
    try {
      // TODO: Implement actual connection testing
      await new Promise(resolve => setTimeout(resolve, 3000));

      const mockResults = {
        webrtc: { status: 'success', message: 'WebRTC bağlantısı başarılı', details: { ice_gathered: 4, turn_working: true } },
        sip: { status: 'success', message: 'SIP kaydı başarılı', details: { registered: true, response_time: '125ms' } },
        media: { status: 'success', message: 'Medya akışı test edildi', details: { audio_quality: 'excellent', video_quality: 'good' } }
      };

      setTestResults(prev => ({ ...prev, [type]: mockResults[type] }));
      toast.success(`${type.toUpperCase()} test başarılı`);
    } catch (err) {
      setTestResults(prev => ({ ...prev, [type]: { status: 'error', message: 'Test başarısız' } }));
      toast.error(`${type.toUpperCase()} test başarısız`);
    } finally {
      setIsTesting(false);
    }
  };

  const addServer = (type: 'stun' | 'turn' | 'sip') => {
    if (!settings) return;

    const newServer: VoIPServerConfig = {
      id: `${type}-${Date.now()}`,
      name: `Yeni ${type.toUpperCase()} Server`,
      type,
      host: '',
      port: type === 'stun' ? 19302 : type === 'turn' ? 3478 : 5060,
      protocol: 'udp',
      is_active: false
    };

    setSettings(prev => prev ? {
      ...prev,
      webrtc: {
        ...prev.webrtc,
        ice_servers: [...prev.webrtc.ice_servers, newServer]
      }
    } : null);
  };

  const removeServer = (serverId: string) => {
    if (!settings) return;

    setSettings(prev => prev ? {
      ...prev,
      webrtc: {
        ...prev.webrtc,
        ice_servers: prev.webrtc.ice_servers.filter(server => server.id !== serverId)
      }
    } : null);
  };

  const updateServer = (serverId: string, updates: Partial<VoIPServerConfig>) => {
    if (!settings) return;

    setSettings(prev => prev ? {
      ...prev,
      webrtc: {
        ...prev.webrtc,
        ice_servers: prev.webrtc.ice_servers.map(server =>
          server.id === serverId ? { ...server, ...updates } : server
        )
      }
    } : null);
  };

  const togglePasswordVisibility = (field: string) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  if (isLoading || !settings) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin h-16 w-16 border-2 border-primary-100 border-t-primary-600 mx-auto mb-4"></div>
          <p className="text-slate-500">VoIP ayarları yükleniyor...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'webrtc', name: 'WebRTC Ayarları', icon: Globe },
    { id: 'sip', name: 'SIP Konfigürasyonu', icon: Phone },
    { id: 'media', name: 'Medya Ayarları', icon: AudioLines },
    { id: 'calls', name: 'Çağrı Yönetimi', icon: PhoneCall },
    { id: 'queue', name: 'Kuyruk Ayarları', icon: Users },
    { id: 'security', name: 'Güvenlik', icon: Shield },
    { id: 'monitoring', name: 'İzleme', icon: BarChart3 }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-primary-600 mb-1">Verwaltung</p>
          <h1 className="text-2xl font-serif font-medium text-slate-900">VoIP Sistem Ayarları</h1>
          <p className="text-slate-500 mt-1">Call center VoIP sistemini tamamen dinamik olarak yapılandırın</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Button
            onClick={() => testConnection(activeTab as any)}
            disabled={isTesting}
            variant="outline"
          >
            <TestTube className={`w-4 h-4 mr-2 ${isTesting ? 'animate-pulse' : ''}`} />
            Test Et
          </Button>
          <Button
            onClick={saveSettings}
            disabled={isSaving}
          >
            <Save className={`w-4 h-4 mr-2 ${isSaving ? 'animate-spin' : ''}`} />
            {isSaving ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border border-slate-200 border-t-2 border-t-primary-600">
        <div className="border-b border-slate-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center transition-colors`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* WebRTC Settings Tab */}
          {activeTab === 'webrtc' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <h3 className="text-lg font-serif font-medium text-slate-900">WebRTC Server Konfigürasyonu</h3>
                <div className="flex gap-2 flex-wrap">
                  <Button onClick={() => addServer('stun')} size="sm" variant="outline">
                    <Plus className="w-4 h-4 mr-1" />
                    STUN Ekle
                  </Button>
                  <Button onClick={() => addServer('turn')} size="sm" variant="outline">
                    <Plus className="w-4 h-4 mr-1" />
                    TURN Ekle
                  </Button>
                </div>
              </div>

              {/* ICE Servers */}
              <div className="space-y-4">
                {settings.webrtc.ice_servers.map((server) => (
                  <div key={server.id} className={`bg-white border border-slate-200 border-t-2 ${serverAccent(server.type)} p-4`}>
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-center space-x-2">
                        <Server className="w-5 h-5 text-primary-600" />
                        <h4 className="font-medium text-slate-900">{server.name}</h4>
                        <span className={`px-2 py-0.5 text-xs font-medium border ${server.is_active ? 'bg-success-50 text-success-700 border-success-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                          {server.is_active ? 'Aktif' : 'Pasif'}
                        </span>
                      </div>
                      <Button
                        onClick={() => removeServer(server.id)}
                        size="sm"
                        variant="ghost"
                        className="text-danger-600 hover:text-danger-800"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <label className={labelCls}>Sunucu Adı</label>
                        <input
                          type="text"
                          value={server.name}
                          onChange={(e) => updateServer(server.id, { name: e.target.value })}
                          className={inputCls}
                        />
                      </div>

                      <div>
                        <label className={labelCls}>Host</label>
                        <input
                          type="text"
                          value={server.host}
                          onChange={(e) => updateServer(server.id, { host: e.target.value })}
                          placeholder="stun.example.com"
                          className={inputCls}
                        />
                      </div>

                      <div>
                        <label className={labelCls}>Port</label>
                        <input
                          type="number"
                          value={server.port}
                          onChange={(e) => updateServer(server.id, { port: parseInt(e.target.value) })}
                          className={inputCls}
                        />
                      </div>

                      <div>
                        <label className={labelCls}>Protokol</label>
                        <select
                          value={server.protocol}
                          onChange={(e) => updateServer(server.id, { protocol: e.target.value as any })}
                          className={inputCls}
                        >
                          <option value="udp">UDP</option>
                          <option value="tcp">TCP</option>
                          <option value="tls">TLS</option>
                        </select>
                      </div>

                      {server.type === 'turn' && (
                        <>
                          <div>
                            <label className={labelCls}>Kullanıcı Adı</label>
                            <input
                              type="text"
                              value={server.username || ''}
                              onChange={(e) => updateServer(server.id, { username: e.target.value })}
                              className={inputCls}
                            />
                          </div>

                          <div>
                            <label className={labelCls}>Şifre</label>
                            <div className="relative">
                              <input
                                type={showPasswords[`${server.id}-password`] ? "text" : "password"}
                                value={server.password || ''}
                                onChange={(e) => updateServer(server.id, { password: e.target.value })}
                                className={`${inputCls} pr-10`}
                              />
                              <button
                                type="button"
                                onClick={() => togglePasswordVisibility(`${server.id}-password`)}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                              >
                                {showPasswords[`${server.id}-password`] ?
                                  <EyeOff className="w-4 h-4 text-slate-400" /> :
                                  <Eye className="w-4 h-4 text-slate-400" />
                                }
                              </button>
                            </div>
                          </div>
                        </>
                      )}

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id={`active-${server.id}`}
                          checked={server.is_active}
                          onChange={(e) => updateServer(server.id, { is_active: e.target.checked })}
                          className={checkboxCls}
                        />
                        <label htmlFor={`active-${server.id}`} className="ml-2 block text-sm text-slate-700">
                          Aktif
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* WebRTC General Settings */}
              <div className="bg-white border border-slate-200 p-4">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-4">Genel WebRTC Ayarları</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className={labelCls}>ICE Gathering Timeout (ms)</label>
                    <input
                      type="number"
                      value={settings.webrtc.gathering_timeout}
                      onChange={(e) => setSettings(prev => prev ? {
                        ...prev,
                        webrtc: { ...prev.webrtc, gathering_timeout: parseInt(e.target.value) }
                      } : null)}
                      className={inputCls}
                    />
                  </div>

                  <div>
                    <label className={labelCls}>Bağlantı Timeout (ms)</label>
                    <input
                      type="number"
                      value={settings.webrtc.connection_timeout}
                      onChange={(e) => setSettings(prev => prev ? {
                        ...prev,
                        webrtc: { ...prev.webrtc, connection_timeout: parseInt(e.target.value) }
                      } : null)}
                      className={inputCls}
                    />
                  </div>

                  <div>
                    <label className={labelCls}>Yeniden Bağlanma Denemesi</label>
                    <input
                      type="number"
                      value={settings.webrtc.reconnect_attempts}
                      onChange={(e) => setSettings(prev => prev ? {
                        ...prev,
                        webrtc: { ...prev.webrtc, reconnect_attempts: parseInt(e.target.value) }
                      } : null)}
                      className={inputCls}
                    />
                  </div>
                </div>
              </div>

              {/* Test Results */}
              {testResults.webrtc && (
                <div className={`border p-4 ${testResults.webrtc.status === 'success' ? 'border-success-200 bg-success-50' : 'border-danger-200 bg-danger-50'}`}>
                  <div className="flex items-center">
                    {testResults.webrtc.status === 'success' ?
                      <CheckCircle className="w-5 h-5 text-success-600 mr-2" /> :
                      <AlertTriangle className="w-5 h-5 text-danger-600 mr-2" />
                    }
                    <h4 className={`font-medium ${testResults.webrtc.status === 'success' ? 'text-success-700' : 'text-danger-700'}`}>
                      Test Sonucu
                    </h4>
                  </div>
                  <p className={`mt-2 ${testResults.webrtc.status === 'success' ? 'text-success-700' : 'text-danger-700'}`}>
                    {testResults.webrtc.message}
                  </p>
                  {testResults.webrtc.details && (
                    <div className="mt-2 text-sm text-slate-600">
                      <pre>{JSON.stringify(testResults.webrtc.details, null, 2)}</pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Other tabs would be implemented similarly... */}
          {activeTab !== 'webrtc' && (
            <div className="text-center py-12">
              <Settings className="w-16 h-16 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-serif font-medium text-slate-900 mb-2">{tabs.find(t => t.id === activeTab)?.name}</h3>
              <p className="text-slate-500">Bu sekme henüz geliştirilme aşamasında...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VoIPSettingsPage;
